import React, { useState } from 'react';
import { partnerApiService } from '../../services/api';
import { DashboardReview } from '../../types/api';
import { getImageUrl } from '../../utils/image';

interface ReviewsPanelProps {
  reviews: DashboardReview[];
  onChange: React.Dispatch<React.SetStateAction<DashboardReview[]>>;
}

/** Пять звёзд оценки; общая для панели отзывов и панели жалоб. */
export const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <span style={{ display: 'inline-flex', gap: 1 }} aria-label={`Оценка ${rating} из 5`}>
    {[0, 1, 2, 3, 4].map((index) => (
      <svg
        key={index}
        width="12"
        height="12"
        viewBox="0 0 20 20"
        fill="currentColor"
        style={{ color: index < rating ? 'var(--qp2-amber)' : 'var(--qp2-line)' }}
        aria-hidden
      >
        <path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.07 3.29a1 1 0 00.95.69h3.46c.97 0 1.37 1.24.59 1.81l-2.8 2.03a1 1 0 00-.37 1.12l1.07 3.3c.3.92-.75 1.68-1.54 1.11l-2.8-2.03a1 1 0 00-1.17 0l-2.8 2.03c-.79.57-1.84-.19-1.54-1.11l1.07-3.3a1 1 0 00-.37-1.12l-2.8-2.03c-.78-.57-.38-1.81.59-1.81h3.46a1 1 0 00.95-.69l1.07-3.29z" />
      </svg>
    ))}
  </span>
);

/**
 * Отзывы и ответы на них.
 *
 * Единственный экран кабинета, где партнёр не настраивает витрину, а
 * разговаривает с клиентом, — поэтому ответ живёт прямо в карточке
 * отзыва, без перехода и модалки.
 */
export const ReviewsPanel: React.FC<ReviewsPanelProps> = ({ reviews, onChange }) => {
  const [replyingId, setReplyingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setReplyingId(null);
    setEditingId(null);
    setText('');
    setError(null);
  };

  const applyReply = (reviewId: number, replyText: string | null, repliedAt: string | null) => {
    onChange((prev) =>
      prev.map((review) =>
        review.id === reviewId ? { ...review, reply_text: replyText, replied_at: repliedAt } : review,
      ),
    );
  };

  const submit = async (reviewId: number, mode: 'create' | 'edit') => {
    const value = text.trim();
    if (!value) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { review } =
        mode === 'create'
          ? await partnerApiService.replyToReview(reviewId, value)
          : await partnerApiService.updateReply(reviewId, value);
      applyReply(reviewId, review.reply_text ?? null, review.replied_at ?? null);
      reset();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось сохранить ответ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeReply = async (reviewId: number) => {
    if (!window.confirm('Удалить ответ на отзыв?')) return;
    try {
      await partnerApiService.deleteReply(reviewId);
      applyReply(reviewId, null, null);
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'Не удалось удалить ответ');
    }
  };

  const editor = (reviewId: number, mode: 'create' | 'edit') => (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Ответ от лица заведения…"
        rows={3}
        maxLength={1000}
        disabled={isSubmitting}
        className="qp-textarea"
      />
      {error && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--qp2-rose)' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="qp-btn qp-btn--primary qp-btn--sm"
          onClick={() => submit(reviewId, mode)}
          disabled={!text.trim() || isSubmitting}
        >
          {isSubmitting ? 'Сохраняем…' : 'Отправить'}
        </button>
        <button type="button" className="qp-btn qp-btn--ghost qp-btn--sm" onClick={reset} disabled={isSubmitting}>
          Отмена
        </button>
      </div>
    </div>
  );

  return (
    <section className="qp-panel">
      <header className="qp-panel-head">
        <h2 className="qp-label">Последние отзывы</h2>
        <span className="qp-strip-text">{reviews.length}</span>
      </header>

      {reviews.length === 0 ? (
        <p className="qp-faint" style={{ margin: 0, fontSize: 13 }}>
          Отзывов пока нет.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reviews.map((review) => (
            <article
              key={review.id}
              style={{
                border: '1px solid var(--qp2-line-soft)',
                borderRadius: 11,
                padding: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span className="qp-avatar" style={{ width: 28, height: 28, fontSize: 10.5 }}>
                  {review.user?.avatar ? (
                    <img
                      src={review.user.avatar}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                    />
                  ) : (
                    review.user?.name?.charAt(0)?.toUpperCase() || 'Г'
                  )}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{review.user?.name || 'Гость'}</div>
                  <Stars rating={review.rating} />
                </div>
                <span className="qp-check-hint">
                  {new Date(review.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                </span>
              </div>

              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--qp2-muted)' }}>
                {review.text}
              </p>

              {review.images && review.images.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {review.images.map((image) => (
                    <img
                      key={image.id}
                      src={getImageUrl(image.url) || image.url}
                      alt=""
                      loading="lazy"
                      style={{
                        width: 72,
                        height: 72,
                        objectFit: 'cover',
                        borderRadius: 9,
                        border: '1px solid var(--qp2-line)',
                      }}
                    />
                  ))}
                </div>
              )}

              {review.reply_text && editingId !== review.id ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 13px',
                    borderRadius: 10,
                    background: 'var(--qp2-jade-soft)',
                    color: 'var(--qp2-jade-ink)',
                    borderLeft: '3px solid var(--qp2-jade)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="qp-label" style={{ color: 'inherit', opacity: 0.75 }}>
                      Ответ заведения
                    </span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        className="qp-link"
                        style={{ color: 'inherit' }}
                        onClick={() => {
                          setEditingId(review.id);
                          setReplyingId(null);
                          setText(review.reply_text || '');
                          setError(null);
                        }}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="qp-link"
                        style={{ color: 'var(--qp2-rose)' }}
                        onClick={() => removeReply(review.id)}
                      >
                        Удалить
                      </button>
                    </span>
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.5 }}>{review.reply_text}</p>
                </div>
              ) : editingId === review.id ? (
                editor(review.id, 'edit')
              ) : replyingId === review.id ? (
                editor(review.id, 'create')
              ) : (
                <button
                  type="button"
                  className="qp-link"
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    setReplyingId(review.id);
                    setEditingId(null);
                    setText('');
                    setError(null);
                  }}
                >
                  Ответить
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
